import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import Sticky from 'react-stickynode';
import CONSTANTS from 'tradera-apps/tradera/components/discover/constants/constants';
import PAGE_TYPES from 'tradera-constants/page-types';
import STRINGS from 'constants/strings';
import * as gtm from 'tradera-apps/tradera/components/discover/helpers/search-gtm';
import { formatNumber } from 'packages';
import { getTotalActiveFilterCount } from '../../helpers/filter';
import { getAttributeIcon } from '../filters/icon-helper';
import {
    getTranslatedAttributeName,
    getTranslatedAttributeValue
} from 'tradera-components/attributes/attribute-helper';

import { useTranslator, withTranslator } from 'tradera-lang/translate';
import uiActions from 'tradera-state/ui/actions';
import ShowMore from 'components/show-more/show-more';
import Breadcrumbs from 'tradera-components/breadcrumbs/breadcrumbs';
import Breakpoint, {
    BREAKPOINTS
} from 'tradera-components/breakpoint/breakpoint';
import FilterCategoriesHorizontal from '../filters/categories/filter-categories-horizontal';
import InternalSearchForm from '../internal-search-form';
import IntroTextContainer from './intro-text-container';
import ItemLayoutSwitch from './layout/item-layout-switch';
import SaveSearchButton from './save-search-button';
import SiteLayoutSwitch from './layout/site-layout-switch';
import SortSelect from './sort/sort-select';
import Tag from './tag';
import CloseIcon from '@tradera/blueprint/theme/icons2/close.svg?jsx';
import DescendingIcon from '@tradera/blueprint/theme/icons/descending.svg?jsx';
import ArrowDownIcon from '@tradera/blueprint/theme/icons2/arrow-down.svg?jsx';
import FilterIcon from '@tradera/blueprint/theme/icons2/filter.svg?jsx';
import { TranslationStrategy } from 'tradera-apps/tradera/components/discover/components/search-results/translation-strategy';
import { useIsTablet } from 'tradera-hooks/use-is-tablet';
import logger from 'packages/logger';

const SearchResultsHeader = props => {
    const {
        featureSwitches,
        handleAttributeFilterChange,
        handleTranslationStrategyClick,
        handleCategoryChange,
        handleClearAllFilters,
        handleFilterChange,
        handleInternalSearch,
        handleModalClick,
        handlePriceFilterChange,
        handleSearchCategoryChange,
        headerCategory,
        internalSearch,
        isLoggedIn,
        openSignIn,
        pageType,
        queryParams,
        searchFormOpen,
        searchResults,
        t
    } = props;

    const {
        filters = {},
        filterPrice,
        attributeFilters = [],
        totalItems,
        activeFilters = [],
        isSavedSearchEmailEnabled
    } = searchResults;
    const { sortBy } = filters;
    const { fromPrice, toPrice } = filterPrice;
    const { headerText } = headerCategory;
    const searchTerm = queryParams.q;
    const userSetPriceFilter =
        fromPrice && (fromPrice.value > 1 || toPrice.value < 5000);

    const totalActiveFilters = getTotalActiveFilterCount(
        fromPrice,
        toPrice,
        attributeFilters,
        activeFilters
    );

    const attributeFiltersEnabled =
        featureSwitches['search-with-attribute-filters'];
    const showBreadcrumb = [
        PAGE_TYPES.SEARCH_RESULT_PAGE,
        PAGE_TYPES.CATEGORY_RESULT_PAGE
    ].includes(pageType);
    const showHeading = pageType !== PAGE_TYPES.CUSTOM_RESULT_PAGE;
    const showIntroText = pageType === PAGE_TYPES.CATEGORY_RESULT_PAGE;
    const showSaveSearch =
        !queryParams.savedQueryName || queryParams.savedQueryName === '';
    const isTablet = useIsTablet();
    const hasAttributeFilters =
        attributeFiltersEnabled &&
        attributeFilters &&
        attributeFilters.length > 0;
    useEffect(() => {
        if (sortBy === undefined) {
            // sortBy should not be undefined. Log this.
            logger(
                'sortBy should not be undefined when rendering search-result-header'
            );
        }
    }, [sortBy]);

    return (
        <header className="search-results-header">
            <Breakpoint mobile>
                <div
                    id="scroll-to-top-anchor"
                    style={{ scrollMarginTop: '82px' }}
                />
            </Breakpoint>

            <Breakpoint upTo={BREAKPOINTS.MD}>
                <Sticky top={searchFormOpen && !isTablet ? 105 : 49}>
                    <div className="row bg-white filters">
                        <div className="sticky-top pl-2 pl-md-3">
                            {showBreadcrumb && (
                                <Breadcrumbs
                                    inSPA={true}
                                    categoryTree={
                                        searchResults?.filterCategories
                                            ?.categoryTree
                                    }
                                    handleClick={
                                        pageType ===
                                            PAGE_TYPES.SEARCH_RESULT_PAGE &&
                                        handleSearchCategoryChange
                                            ? handleSearchCategoryChange
                                            : null
                                    }
                                />
                            )}

                            <button
                                className={`border-left position-relative pl-2 my-1 btn unbutton ${
                                    totalActiveFilters > 0 ? 'pr-1' : ''
                                }`}
                                onClick={() => {
                                    handleModalClick(
                                        CONSTANTS.MODALS.FILTER_MODAL
                                    );
                                }}>
                                <FilterIcon className="icon icon-before icon-dark my-auto" />
                                <span className="align-middle text-primary">
                                    {t('searchResults_filter')}
                                </span>
                                {totalActiveFilters > 0 && (
                                    <span className="badge badge-primary filters__active_badge">
                                        {totalActiveFilters}
                                    </span>
                                )}
                            </button>
                        </div>
                        <div className="col-12 d-flex pl-2 pl-md-3 pr-0 py-1 filters__categories">
                            <button
                                className="btn btn-tag btn-outline-dark mr-1 position-relative text-nowrap"
                                onClick={() => {
                                    handleModalClick(
                                        CONSTANTS.MODALS.CATEGORY_MODAL
                                    );
                                }}>
                                <span>
                                    {t('searchResults_categories_more')}
                                </span>
                                <ArrowDownIcon className="icon icon-sm icon-dark align-middle ml-1" />
                            </button>
                            <FilterCategoriesHorizontal
                                filterCategories={
                                    searchResults?.filterCategories
                                }
                                handleCategoryChange={handleCategoryChange}
                                handleSearchCategoryChange={
                                    handleSearchCategoryChange
                                }
                                pageType={pageType}
                                queryParams={queryParams}
                            />
                        </div>
                        {hasAttributeFilters && (
                            <div className="col-12 filters__attributes d-flex bg-light pl-1 pl-md-2 overflow-x-auto hide-scrollbar">
                                <AttributeFilterTagButtons
                                    attributeFilters={attributeFilters}
                                    handleModalClick={handleModalClick}
                                />
                            </div>
                        )}
                    </div>
                </Sticky>
                {internalSearch.showSearchBar && (
                    <div className="search-results-header__heading search-results-header__internal-search-wrapper">
                        <InternalSearchForm
                            internalSearch={internalSearch}
                            handleInternalSearch={handleInternalSearch}
                        />
                    </div>
                )}
            </Breakpoint>

            <div
                className={`${
                    internalSearch.showSearchBar
                        ? ''
                        : 'search-results-header__heading'
                } row justify-content-between pb-2 pb-lg-1 ${
                    showHeading ? 'pt-lg-1 pb-lg-0' : ''
                } ${
                    hasAttributeFilters
                        ? 'search-results-header__heading--with-attributes'
                        : ''
                }`}>
                {showHeading && (
                    <>
                        <TranslationStrategy
                            handleTranslationStrategyClick={
                                handleTranslationStrategyClick
                            }>
                            <h1
                                className={`${
                                    pageType === PAGE_TYPES.CATEGORY_RESULT_PAGE
                                        ? 'heading-madrid'
                                        : 'heading-london'
                                } text-styled mb-0`}
                                data-test-search-result-heading>
                                {headerText}
                            </h1>
                        </TranslationStrategy>

                        {showSaveSearch && (
                            <Breakpoint upTo={BREAKPOINTS.MD}>
                                <div className="col-12 d-flex justify-content-center align-items-center">
                                    <SaveSearchButton
                                        queryParams={queryParams}
                                        isLoggedIn={isLoggedIn}
                                        isSavedSearchEmailEnabled={
                                            isSavedSearchEmailEnabled
                                        }
                                        searchName={searchTerm || headerText}
                                        categoryId={headerCategory.categoryId}
                                        openSignIn={openSignIn}
                                    />
                                </div>
                            </Breakpoint>
                        )}
                        {showIntroText && (
                            <ContentDescription
                                introText={headerCategory?.introText}
                                className="col-12 mt-1 text-styled"
                            />
                        )}
                    </>
                )}

                <Breakpoint upTo={BREAKPOINTS.MD}>
                    <div className="col-12 row no-gutters mt-3">
                        <div className="col d-flex flex-wrap justify-content-center bg-light py-2 px-0">
                            <span>
                                {t('searchResults_itemsFound', {
                                    itemsCount: formatNumber(totalItems),
                                    count: totalItems
                                })}
                            </span>
                            <SortingLink
                                sortBy={sortBy}
                                handleModalClick={handleModalClick}
                                t={t}
                            />
                        </div>
                        <span className="col-12 text-center">
                            {totalActiveFilters > 0
                                ? `${t('searchResults_activeFilters')}:`
                                : null}
                        </span>
                    </div>
                </Breakpoint>
            </div>

            <Breakpoint downTo={BREAKPOINTS.LG}>
                <div className="d-flex pt-1 2 pb-3 align-items-baseline">
                    <span className="mr-2">
                        {totalItems
                            ? t('searchResults_itemsFound', {
                                  itemsCount: formatNumber(totalItems),
                                  count: totalItems
                              })
                            : t('searchResults_noItemsFound')}
                        {' - '}
                    </span>
                    <div className="mr-3">
                        <SortSelect
                            sortBy={sortBy}
                            handleFilterChange={(filterType, value) => {
                                gtm.trackFilter(filterType, value);
                                handleFilterChange(filterType, value);
                            }}
                        />
                    </div>
                    <div className="d-flex align-self-center">
                        <ItemLayoutSwitch
                            id="search-result-header-itemLayout"
                            className="form-control-radio-switch-sm"
                        />
                    </div>
                    <div className="d-none d-xl-flex align-self-center ml-3">
                        <SiteLayoutSwitch
                            id="search-result-header-siteLayout"
                            className="form-control-radio-switch-sm"
                        />
                    </div>
                </div>
            </Breakpoint>

            {totalActiveFilters > 0 && (
                <div className="row search-results-header__active_filters d-flex flex-nowrap flex-md-wrap align-items-center pb-3">
                    <span className="d-none d-md-inline col-auto font-weight-bold">
                        {`${t('searchResults_activeFilters')}: `}
                    </span>
                    {
                        <FilterTags
                            activeFilters={activeFilters}
                            handleFilterChange={handleFilterChange}
                        />
                    }
                    {
                        <AttributeFilterTags
                            attributeFilters={attributeFilters}
                            handleAttributeFilterChange={
                                handleAttributeFilterChange
                            }
                        />
                    }
                    {
                        <PriceFilterTag
                            userSetPriceFilter={userSetPriceFilter}
                            filterPrice={filterPrice}
                            handlePriceFilterChange={handlePriceFilterChange}
                            t={t}
                        />
                    }
                    <button
                        className="btn btn-md-sm btn-outline-danger col-auto m-1"
                        onClick={() => {
                            gtm.trackButton('Remove all filters');
                            handleClearAllFilters();
                        }}>
                        {t('searchResults_clear')}
                        <CloseIcon
                            style={{
                                marginLeft: '0.5rem'
                            }}
                            className="icon icon-xs align-middle"
                        />
                    </button>
                </div>
            )}
        </header>
    );
};

SearchResultsHeader.propTypes = {
    activeFilters: PropTypes.array,
    attributeFilters: PropTypes.array,
    filterPrice: PropTypes.object,
    filters: PropTypes.object,
    handleAttributeFilterChange: PropTypes.func.isRequired,
    handleClearAllFilters: PropTypes.func.isRequired,
    handleFilterChange: PropTypes.func.isRequired,
    handleModalClick: PropTypes.func.isRequired,
    handlePriceFilterChange: PropTypes.func.isRequired,
    headerCategory: PropTypes.object,
    isLoggedIn: PropTypes.bool,
    openSignIn: PropTypes.func,
    pageType: PropTypes.string.isRequired,
    queryParams: PropTypes.object
};

const ContentDescription = ({ introText, ...otherProps }) => {
    return introText ? (
        <div {...otherProps}>
            <Breakpoint upTo={BREAKPOINTS.MD}>
                <ShowMore>
                    <IntroTextContainer html={introText} />
                </ShowMore>
            </Breakpoint>
            <Breakpoint desktop>
                <IntroTextContainer html={introText} />
            </Breakpoint>
        </div>
    ) : null;
};

const FilterTags = ({ activeFilters, handleFilterChange }) => {
    if (!activeFilters) {
        return null;
    }
    return activeFilters.map((filter, index) => {
        const { parameter, options } = filter;
        const selectedName = options.find(
            option => option.value === filter.selectedValue
        ).name;
        const defaultParam = options[0].value;
        return (
            <Tag
                key={index}
                text={selectedName}
                onClick={() => {
                    gtm.trackButton('Remove filter');
                    handleFilterChange(parameter, defaultParam);
                }}
            />
        );
    });
};

const SortingLink = ({ sortBy, handleModalClick, t }) => {
    const selectedSorting = sortBy?.options?.find(
        option => option.value === sortBy.selectedValue
    )?.name;

    if (!selectedSorting) return null;

    const sortLabel = t('searchResults_sortLabel');

    return (
        <div
            onClick={() => {
                handleModalClick(
                    CONSTANTS.MODALS.FILTER_MODAL,
                    STRINGS.DISCOVER.FILTERS.SORTING
                );
            }}>
            <span className="ml-2">{`- ${sortLabel}:`}</span>
            <span className="text-primary mx-2">{selectedSorting}</span>
            <DescendingIcon className="icon align-middle" />
        </div>
    );
};

const PriceFilterTag = ({
    userSetPriceFilter,
    filterPrice,
    handlePriceFilterChange,
    t
}) => {
    const { fromPrice, toPrice } = filterPrice;
    if (userSetPriceFilter) {
        const priceText =
            toPrice.value < 5000
                ? t('searchResults_priceFilterLabel_interval', {
                      fromPrice: fromPrice.value,
                      toPrice: toPrice.value
                  })
                : t('searchResults_priceFilterLabel_above', {
                      fromPrice: fromPrice.value
                  });
        return (
            <Tag text={priceText} onClick={() => handlePriceFilterChange()} />
        );
    } else {
        return null;
    }
};

const AttributeFilterTags = ({
    attributeFilters,
    handleAttributeFilterChange
}) => {
    const { t } = useTranslator();
    if (!attributeFilters) {
        return null;
    }
    return attributeFilters.map(attributeFilter => {
        const { parameter, selectedValues, options = [] } = attributeFilter;
        return options.map((attribute, index) => {
            const selected = selectedValues.includes(attribute.value);
            const text = attributeFilter.translateOptions
                ? getTranslatedAttributeValue(parameter, attribute.value, t)
                : attribute.name;
            return (
                selected && (
                    <Tag
                        key={index}
                        text={text}
                        onClick={() => {
                            handleAttributeFilterChange(
                                parameter,
                                attribute.value
                            );
                        }}
                    />
                )
            );
        });
    });
};

const AttributeFilterTagButtons = ({ attributeFilters, handleModalClick }) => {
    const { t } = useTranslator();

    if (!attributeFilters) {
        return null;
    }

    return attributeFilters.map(attributeFilter => (
        <button
            key={attributeFilter.parameter}
            className={`btn btn-light text-nowrap position-relative ${
                attributeFilter.selectedValues.length > 0 ? 'pl-1 pr-2' : 'px-1'
            }`}
            onClick={() => {
                handleModalClick(
                    CONSTANTS.MODALS.FILTER_MODAL,
                    attributeFilter.parameter
                );
            }}>
            {getAttributeIcon(attributeFilter.parameter, {
                className: 'icon-before'
            })}
            {getTranslatedAttributeName(attributeFilter.parameter, t)}
            {attributeFilter.selectedValues.length > 0 && (
                <span
                    className="badge badge-primary filters__active_badge"
                    style={{
                        top: '.5rem',
                        right: 0
                    }}>
                    {attributeFilter.selectedValues.length}
                </span>
            )}
        </button>
    ));
};

const mapStateToProps = state => {
    return {
        breadcrumbs: state.page.breadcrumbs,
        featureSwitches: state.environment.featureSwitches,
        isLoggedIn: state.member.isLoggedIn,
        searchResults: state.discover,
        searchFormOpen: state.ui.showSearchForm,
        internalSearch: state.discover.internalSearch,
        queryParams: state.discover.queryParams
    };
};

const mapDispatchToProps = dispatch => {
    return {
        openSignIn: redirectUrl => dispatch(uiActions.signIn({ redirectUrl }))
    };
};

export { SearchResultsHeader };
export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withTranslator(SearchResultsHeader));
